import type { AxiosInstance } from 'axios';
import { makeObservable, observable } from 'mobx';

import type { IDashboardService, Wellbore } from '../../shared/api/IDashboardService';
import type { IWellDirectoryService } from '../../shared/api/IWellDirectoryService';
import type { IDirectoriesService } from '../../shared/IDirectoriesService';
import type { INotificationsService } from '../../shared/INotificationsService';
import { Wellbores } from '../../shared/stores/WellboresStore';
import type { TUserData } from '../../shared/types';
import { AlertRulesTemplatesManager } from '../alert-rules-table/services/AlertRulesTemplatesManager';
import { AlertRulesService } from '../alert-rules-table/services/AlertRulesService';
import { AutoAnomaliesService } from '../auto-anomalies/AutoAnomaliesService';
import { ManualAnomaliesService } from '../manual-anomalies/services/ManualAnomaliesService';
import type { WidgetView } from '../types';

import type { SettingsStore } from './SettingsStore';

class AlertRulesView {
  service: AlertRulesService;

  dispose: VoidFunction | null = null;

  constructor(
    wellId: number,
    index: 'time' | 'depth',
    agent: AxiosInstance,
    notifications: INotificationsService,
    directoryService: IDirectoriesService,
    dashboardService: IDashboardService,
    wellDirectoryService: IWellDirectoryService,
    userData: TUserData,
    settingsStore: SettingsStore,
    wellbores: Wellbores,
  ) {
    const templatesManager = new AlertRulesTemplatesManager(index, () => userData.sub, dashboardService);
    this.service = new AlertRulesService(
      wellId,
      index,
      agent,
      notifications,
      directoryService,
      wellDirectoryService,
      userData,
      settingsStore,
      wellbores,
      templatesManager,
    );
  }

  init() {
    this.dispose = this.service.init();
  }

  destroy() {
    this.dispose?.();
  }
}

export class AutoAnomaliesViewController {
  service: AutoAnomaliesService;

  dispose: VoidFunction | null = null;

  constructor(
    wellId: number,
    agent: AxiosInstance,
    _notifications: INotificationsService,
    settingsStore: SettingsStore,
    wellbores: Wellbores,
    directoryService: IDirectoriesService,
    getAccessToken: () => string | null,
  ) {
    this.service = new AutoAnomaliesService(wellId, agent, settingsStore, wellbores, directoryService, getAccessToken);
  }

  init() {
    this.dispose = this.service.init();
  }

  destroy() {
    this.dispose?.();
  }
}

export class ManualAnomaliesViewController {
  service: ManualAnomaliesService;

  dispose: VoidFunction | null = null;

  constructor(
    wellId: number,
    agent: AxiosInstance,
    notifications: INotificationsService,
    userData: TUserData,
    settingsStore: SettingsStore,
    wellbores: Wellbores,
    directoryService: IDirectoriesService,
  ) {
    this.service = new ManualAnomaliesService(
      wellId,
      agent,
      notifications,
      userData,
      settingsStore,
      wellbores,
      directoryService,
    );
  }

  init() {
    this.dispose = this.service.init();
  }

  destroy() {
    this.dispose?.();
  }
}

export type ViewStoreCurrent =
  | { name: 'alertRulesTime' | 'alertRulesDepth'; view: AlertRulesView }
  | { name: 'autoAnomalies'; view: AutoAnomaliesViewController }
  | { name: 'manualAnomalies'; view: ManualAnomaliesViewController };

export class ViewStore {
  current!: ViewStoreCurrent;

  wellbores: Wellbores;

  constructor(
    private readonly wellId: number,
    private readonly dashboardService: IDashboardService,
    private readonly settingsStore: SettingsStore,
    private readonly agent: AxiosInstance,
    private readonly notifications: INotificationsService,
    private readonly directoryService: IDirectoriesService,
    private readonly wellDirectoryService: IWellDirectoryService,
    private readonly userData: TUserData,
    private readonly getAccessToken: () => string | null,
    onWellboreChange?: (wellbore: Wellbore) => void,
  ) {
    this.wellbores = new Wellbores(dashboardService, onWellboreChange);
    this.select(settingsStore.view);
    makeObservable(this, {
      current: observable.ref,
      wellbores: observable.ref,
    });
  }

  select(view: WidgetView) {
    const normalizedView: WidgetView = view;

    if (this.current?.name === normalizedView) {
      return;
    }

    if (this.current) {
      this.current.view.destroy();
    }

    if (normalizedView === 'alertRulesTime') {
      this.current = {
        name: 'alertRulesTime',
        view: new AlertRulesView(
          this.wellId,
          'time',
          this.agent,
          this.notifications,
          this.directoryService,
          this.dashboardService,
          this.wellDirectoryService,
          this.userData,
          this.settingsStore,
          this.wellbores,
        ),
      };
    } else if (normalizedView === 'alertRulesDepth') {
      this.current = {
        name: 'alertRulesDepth',
        view: new AlertRulesView(
          this.wellId,
          'depth',
          this.agent,
          this.notifications,
          this.directoryService,
          this.dashboardService,
          this.wellDirectoryService,
          this.userData,
          this.settingsStore,
          this.wellbores,
        ),
      };
    } else if (normalizedView === 'autoAnomalies') {
      this.current = {
        name: 'autoAnomalies',
        view: new AutoAnomaliesViewController(
          this.wellId,
          this.agent,
          this.notifications,
          this.settingsStore,
          this.wellbores,
          this.directoryService,
          this.getAccessToken,
        ),
      };
    } else if (normalizedView === 'manualAnomalies') {
      this.current = {
        name: 'manualAnomalies',
        view: new ManualAnomaliesViewController(
          this.wellId,
          this.agent,
          this.notifications,
          this.userData,
          this.settingsStore,
          this.wellbores,
          this.directoryService,
        ),
      };
    } else {
      this.current = {
        name: 'alertRulesTime',
        view: new AlertRulesView(
          this.wellId,
          'time',
          this.agent,
          this.notifications,
          this.directoryService,
          this.dashboardService,
          this.wellDirectoryService,
          this.userData,
          this.settingsStore,
          this.wellbores,
        ),
      };
    }

    this.settingsStore.setTableView(normalizedView);
    this.current.view.init();
  }
}
